/**
 * ChatSnippetCard — minimal Apple-like actionable card rendered inside the
 * chat stream (not in a popup). Wires the CTA to the REAL feature endpoint:
 *   - karma.create  → POST /api/karma   (prefill title + why)
 *   - sankalp.create → POST /api/sankalp/propose + /accept
 *   - Add to Life Journey → POST /api/profile/life_events
 *
 * Shows exactly ONE primary CTA per snippet (and one secondary dismiss).
 * After successful save, replaces itself with a quiet "Saved ✓" chip.
 */
import React, { useState } from "react";
import { Target, Flame, Compass, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/hooks/useAuth";

const KIND_META = {
  GOOD_TIME_FOR: {
    Icon: Compass,
    accent: "linear-gradient(135deg, #B384FF 0%, #FF9BD2 100%)",
    label: "Good Time For",
  },
  TAKE_SANKALP: {
    Icon: Flame,
    accent: "linear-gradient(135deg, #FFC56B 0%, #FF9BD2 100%)",
    label: "Take Sankalp",
  },
};

export default function ChatSnippetCard({ snippet, onDismiss, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null); // {label, destination}
  if (!snippet || !snippet.cta) return null;
  const kind = snippet.kind;
  const meta = KIND_META[kind] || { Icon: Target, accent: "linear-gradient(135deg,#7E3BFF,#B384FF)", label: snippet.title };
  const { Icon } = meta;

  const execute = async () => {
    if (busy || saved) return;
    setBusy(true);
    try {
      const t = snippet.cta.target;
      const pf = snippet.cta.prefill || {};
      if (t === "karma.create") {
        const { data } = await api.post("/karma", {
          title: pf.title || snippet.action || "A small positive action",
          why: pf.description || snippet.astro_reason || snippet.reason || "",
          status: "active",
          source_prediction_id: pf.source_prediction_id || snippet.source_prediction_id,
        });
        setSaved({ label: "Karma created", id: data?.id });
        toast.success("Karma saved to your journey.");
        try {
          await api.post("/kalki/snippet/saved", {
            snippet_id: snippet.snippet_id,
            semantic_key: snippet.semantic_key,
            semantic_fingerprint: snippet.semantic_fingerprint,
            feature: "karma",
            feature_id: data?.id,
          });
        } catch (_) {}
        onSaved?.({ kind, id: data?.id, feature: "karma" });
      } else if (t === "sankalp.create") {
        const payload = {
          statement: pf.intention || snippet.body || "Daily step forward",
          reason: snippet.astro_reason || snippet.reason || "",
          duration_days: pf.duration_days || 7,
          source_prediction_id: pf.source_prediction_id || snippet.source_prediction_id,
        };
        const { data } = await api.post("/sankalp/propose", payload);
        try { await api.post(`/sankalp/${data.id}/accept`); } catch(_) {}
        setSaved({ label: "Sankalp taken", id: data?.id });
        toast.success("Sankalp taken. Progress begins today.");
        try {
          await api.post("/kalki/snippet/saved", {
            snippet_id: snippet.snippet_id,
            semantic_key: snippet.semantic_key,
            semantic_fingerprint: snippet.semantic_fingerprint,
            feature: "sankalp",
            feature_id: data?.id,
          });
        } catch (_) {}
        onSaved?.({ kind, id: data?.id, feature: "sankalp" });
      } else if (t === "karma.view" || t === "sankalp.view") {
        // "View Progress" CTA — open the existing record instead of creating.
        const feature = t.split(".")[0];
        const recordId = pf.feature_id;
        setSaved({ label: feature === "sankalp" ? "Progress opened" : "Karma opened", id: recordId });
        toast(feature === "sankalp" ? "Opened your active Sankalp." : "Opened your active Karma.");
        onSaved?.({ kind, id: recordId, feature });
      } else if (t === "life_journey.add") {
        toast("Opened Life Journey.");
        try {
          await api.post("/kalki/snippet/saved", {
            snippet_id: snippet.snippet_id,
            semantic_key: snippet.semantic_key,
            semantic_fingerprint: snippet.semantic_fingerprint,
            feature: "life_journey",
          });
        } catch (_) {}
        onSaved?.({ kind, feature: "life_journey" });
      } else {
        toast("Feature opened.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div
        data-testid={`chat-snippet-saved-${(kind || "").toLowerCase()}`}
        style={{
          marginTop: 10, padding: "10px 14px",
          borderRadius: 14,
          background: "rgba(21,184,129,0.12)",
          border: "1px solid rgba(21,184,129,0.28)",
          color: "#8EE6C2", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8,
        }}
      >
        <Check size={14} /> {saved.label}
      </div>
    );
  }

  return (
    <div
      data-testid={`chat-snippet-${(kind || "").toLowerCase()}`}
      style={{
        marginTop: 12,
        padding: "14px 16px",
        borderRadius: 16,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(179,132,255,0.26)",
        color: "#F6F1FF",
        fontFamily: "'Inter', sans-serif",
        maxWidth: 420,
        boxShadow: "0 10px 30px rgba(55,0,170,0.22)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flex: "none",
          background: meta.accent,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 14px rgba(93,0,255,0.35)",
        }}>
          <Icon size={16} color="#FFF" strokeWidth={1.8} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
                        color: "#CFC2FA", fontWeight: 500 }}>{snippet.title || meta.label}</div>
          <div style={{ fontSize: 14, lineHeight: 1.45, marginTop: 3, color: "#F6F1FF" }}>
            {snippet.body || snippet.action || snippet.title}
          </div>
          {snippet.astro_reason && (
            <div
              data-testid={`chat-snippet-astro-reason-${(kind || "").toLowerCase()}`}
              style={{ fontSize: 11, color: "rgba(230,220,255,0.78)", marginTop: 6,
                        fontStyle: "italic", lineHeight: 1.45 }}
            >
              {snippet.astro_reason}
            </div>
          )}
          {snippet.window && (
            <div style={{ fontSize: 11, color: "rgba(230,220,255,0.6)", marginTop: 4 }}>
              Window: {snippet.window}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button
              data-testid={`chat-snippet-cta-${(kind || "").toLowerCase()}`}
              disabled={busy}
              onClick={execute}
              style={{
                padding: "8px 14px", borderRadius: 999,
                background: busy ? "rgba(126,59,255,0.4)" : "linear-gradient(135deg,#7E3BFF,#B384FF)",
                color: "#FFF", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
                border: "none", cursor: busy ? "wait" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {busy && <Loader2 size={12} className="animate-spin" />}
              {snippet.cta.label || "Continue"}
            </button>
            <button
              data-testid={`chat-snippet-dismiss-${(kind || "").toLowerCase()}`}
              onClick={async () => {
                try {
                  await api.post("/kalki/snippet/dismiss", {
                    snippet_id: snippet.snippet_id,
                    semantic_key: snippet.semantic_key,
                    semantic_fingerprint: snippet.semantic_fingerprint,
                  });
                } catch (_) {}
                onDismiss?.();
              }}
              style={{
                padding: "8px 10px", borderRadius: 999,
                background: "transparent", color: "rgba(230,220,255,0.65)",
                fontSize: 11, letterSpacing: "0.05em", border: "none", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <X size={12} /> Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
